// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Client-side calorie target estimate for onboarding step 4 ("your starting
 * point"). Mirrors decisions.md's calorie-floor rule exactly:
 *
 *   "Targets come from Mifflin-St Jeor. The daily calorie target floor is
 *   max(computed BMR, 1,200 kcal female / 1,500 kcal male); targets below
 *   the floor (manual or computed) are rejected with the correction flow,
 *   never silently raised. Manual targets above the floor require explicit
 *   acknowledgement."
 *
 * ADR-027 rule 7 activity/energy model: mirrors
 * infra-cdk/lambdas/bitebalance-api/shared/targets.ts's TDEE calculation
 * (device path / activity+sports path / legacy fixed-multiplier path) so
 * the live preview shown here matches what PUT /v1/onboarding/4 will
 * actually persist server-side. `activity` is OPTIONAL and defaults to the
 * legacy path so every existing call site (OnboardingPage.tsx's floor
 * check, the below-floor blocker) keeps working unchanged — only
 * Step4Body/ActivitySection pass a real activity input.
 *
 * The server remains authoritative (ADR-011/012: targets are computed only
 * server-side) — this is a preview only, duplicated here because there is
 * nowhere in the T04a contract's Step4Schema body-inputs answer to compute
 * it centrally before submission.
 */

export type Sex = "female" | "male" | null

export interface BmrInputs {
  sex: Sex
  age: number | null
  heightCm: number | null
  weightKg: number | null
}

export type GoalType = "maintain" | "loss" | "gain" | "improve_health"

export type ActivityLevel = "sedentary" | "light" | "moderate" | "high"
export type SportType =
  | "strength"
  | "running"
  | "cycling"
  | "swimming"
  | "team_sports"
  | "yoga_pilates"
  | "walking_hiking"
  | "other"
export type SportIntensity = "low" | "moderate" | "high"

export interface SportEntryInput {
  type: SportType
  sessionsPerWeek: number
  intensity: SportIntensity
  minutesPerSession: number | null
}

export interface ActivityPreviewInput {
  level: ActivityLevel | null
  sports: SportEntryInput[]
  /** UX-only (does not affect the calculation below — see shared/targets.ts); carried here so this is the one shared draft/preview shape the onboarding UI needs. */
  wearableKcalEnabled: boolean
  avgDeviceActiveKcal: number | null
}

/** "Not answered yet" — same semantics as the server's LEGACY_ACTIVITY. */
export const LEGACY_ACTIVITY: ActivityPreviewInput = {
  level: null,
  sports: [],
  wearableKcalEnabled: false,
  avgDeviceActiveKcal: null,
}

const LEGACY_ACTIVITY_FACTOR = 1.375

/** NEAT-only occupational/daily-movement multiplier — see shared/targets.ts's file header for why these are lower than the traditional Mifflin PAL bands (structured exercise is `sports`, summed separately). */
const BASE_ACTIVITY_FACTOR: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.3,
  moderate: 1.4,
  high: 1.5,
}

/** MET values per (sport type, intensity) — same table as shared/targets.ts (Ainsworth et al. 2011 Compendium of Physical Activities, conservative rounded approximations). */
const MET_TABLE: Record<SportType, Record<SportIntensity, number>> = {
  strength: { low: 3.5, moderate: 5.0, high: 6.0 },
  running: { low: 8.3, moderate: 9.8, high: 11.8 },
  cycling: { low: 4.0, moderate: 8.0, high: 10.0 },
  swimming: { low: 5.8, moderate: 8.3, high: 9.8 },
  team_sports: { low: 4.5, moderate: 7.0, high: 8.0 },
  yoga_pilates: { low: 2.5, moderate: 3.0, high: 4.0 },
  walking_hiking: { low: 3.0, moderate: 4.3, high: 6.0 },
  other: { low: 3.0, moderate: 5.0, high: 7.0 },
}

const SPORT_DEFAULT_MINUTES = 45
const SPORT_DAILY_KCAL_CAP = 600
const DEVICE_KCAL_CAP = 1200

function sportDailyKcal(sports: SportEntryInput[], weightKg: number): number {
  let weeklyKcal = 0
  for (const sport of sports) {
    const met = MET_TABLE[sport.type][sport.intensity]
    const minutes = sport.minutesPerSession ?? SPORT_DEFAULT_MINUTES
    weeklyKcal += ((met * 3.5 * weightKg) / 200) * minutes * sport.sessionsPerWeek
  }
  return Math.min(weeklyKcal / 7, SPORT_DAILY_KCAL_CAP)
}

function tdeeFromActivity(bmr: number, weightKg: number, activity: ActivityPreviewInput): number {
  if (activity.avgDeviceActiveKcal !== null && activity.avgDeviceActiveKcal > 0) {
    return bmr + Math.min(activity.avgDeviceActiveKcal, DEVICE_KCAL_CAP)
  }
  if (activity.level !== null) {
    return bmr * BASE_ACTIVITY_FACTOR[activity.level] + sportDailyKcal(activity.sports, weightKg)
  }
  return bmr * LEGACY_ACTIVITY_FACTOR
}

const FLOOR_FEMALE_KCAL = 1200
const FLOOR_MALE_KCAL = 1500

// --- ADR-030 breastfeeding energy model (Option B, 2026-08-27) -----------
// Mirrors infra-cdk/lambdas/bitebalance-api/shared/targets.ts's constants
// exactly — keep in lockstep. Population-level planning estimates, not
// clinical advice; the coaching copy always frames them as "confirm with
// your doctor/midwife/lactation-aware dietitian". NOTE: this onboarding
// preview cannot actually receive isBreastfeeding=true today — health
// conditions (wire step 13) are declared AFTER body metrics/goal (wire
// steps 3/4) in VISUAL_STEP_ORDER, so the step-4 preview never has the
// answer yet. The parameters exist for numeric lockstep with the server
// (which DOES receive it, via a step-13 recompute) and for any future
// caller (e.g. a post-onboarding profile-edit preview) that has the data.
export const LACTATION_ALLOWANCE_KCAL = 330
export const BREASTFEEDING_FLOOR_KCAL = 1800
export const BREASTFEEDING_MAX_DEFICIT_KCAL_PER_DAY = 500

/** Mifflin-St Jeor BMR. Returns null when a required input is missing. */
export function computeBmr({ sex, age, heightCm, weightKg }: BmrInputs): number | null {
  if (age === null || heightCm === null || weightKg === null || sex === null) return null
  if (age <= 0 || heightCm <= 0 || weightKg <= 0) return null
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age
  return sex === "male" ? base + 5 : base - 161
}

/**
 * Safe daily-calorie floor for this profile: max(computed BMR, sex floor).
 * When sex is unknown/unanswered, the stricter (higher) floor is used so
 * the guardrail never under-protects — see decisions.md ADR-011 item 3.
 * ADR-030: for a breastfeeding user the sex-based floor is REPLACED by
 * BREASTFEEDING_FLOOR_KCAL (never below it regardless of sex).
 */
export function calorieFloor(inputs: BmrInputs, isBreastfeeding = false): number {
  const bmr = computeBmr(inputs)
  if (isBreastfeeding) return Math.round(Math.max(bmr ?? 0, BREASTFEEDING_FLOOR_KCAL))
  const sexFloor =
    inputs.sex === "male"
      ? FLOOR_MALE_KCAL
      : inputs.sex === "female"
        ? FLOOR_FEMALE_KCAL
        : FLOOR_MALE_KCAL
  return Math.round(Math.max(bmr ?? 0, sexFloor))
}

export interface CalorieTargetEstimate {
  /** Suggested target before the floor is applied, or null if not enough data. */
  rawKcal: number | null
  /** The safe minimum for this profile. */
  floorKcal: number
  /** rawKcal clamped to the floor when computable; null if rawKcal is null. */
  suggestedKcal: number | null
  belowFloor: boolean
}

// Review I3 (2026-08-26): MULTIPLICATIVE factors, identical to the
// server's shared/targets.ts (x0.85 loss / x1.10 gain) — the previous
// absolute -500/+300 preview could sit >200 kcal from the persisted
// target on the same screen as the "this is an estimate" copy.
const GOAL_FACTOR: Record<GoalType, number> = {
  maintain: 1,
  loss: 0.85,
  gain: 1.1,
  // improve_health has no weight direction — maintenance calories,
  // matching the server's shared/targets.ts mapping.
  improve_health: 1,
}

/**
 * Computes a suggested calculated target for the given body inputs, goal,
 * and activity/energy declaration. ADR-030: when isBreastfeeding is true,
 * the lactation allowance is added to TDEE for every goal, and a "loss"
 * goal's deficit is capped (mirrors targets.ts's computeTargets exactly —
 * see that file for the full rationale).
 */
export function estimateCalorieTarget(
  inputs: BmrInputs,
  goalType: GoalType,
  activity: ActivityPreviewInput = LEGACY_ACTIVITY,
  isBreastfeeding = false
): CalorieTargetEstimate {
  const bmr = computeBmr(inputs)
  const floorKcal = calorieFloor(inputs, isBreastfeeding)
  if (bmr === null) {
    return { rawKcal: null, floorKcal, suggestedKcal: null, belowFloor: false }
  }
  const tdee = tdeeFromActivity(bmr, inputs.weightKg ?? 0, activity)
  const lactationAllowanceKcal = isBreastfeeding ? LACTATION_ALLOWANCE_KCAL : 0
  const tdeeAdj = tdee + lactationAllowanceKcal

  let rawKcal: number
  if (isBreastfeeding && goalType === "loss") {
    const standardLoss = Math.round((tdeeAdj * GOAL_FACTOR.loss) / 10) * 10
    const cappedLoss = Math.round((tdeeAdj - BREASTFEEDING_MAX_DEFICIT_KCAL_PER_DAY) / 10) * 10
    rawKcal = Math.max(standardLoss, cappedLoss)
  } else {
    rawKcal = Math.round((tdeeAdj * GOAL_FACTOR[goalType]) / 10) * 10
  }
  const belowFloor = rawKcal < floorKcal
  return { rawKcal, floorKcal, suggestedKcal: belowFloor ? floorKcal : rawKcal, belowFloor }
}

/** Evaluates a manually-entered target against the same floor rule. */
export function evaluateManualTarget(
  manualKcal: number,
  inputs: BmrInputs,
  isBreastfeeding = false
): { floorKcal: number; belowFloor: boolean } {
  const floorKcal = calorieFloor(inputs, isBreastfeeding)
  return { floorKcal, belowFloor: manualKcal < floorKcal }
}
